import React, { useEffect, useState } from 'react';
import { Amplify } from 'aws-amplify';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { KeycloakProvider, useKeycloak } from './contexts/KeycloakContext';
import { getAwsConfig } from './aws-config';
import S3Browser from './components/S3Browser';
import LoginCallback from './components/LoginCallback';
import KeycloakDebug from './components/KeycloakDebug';
import './App.css';

// Material-UI 테마
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

// 로딩 컴포넌트
const LoadingScreen = ({ message = "Loading..." }) => (
  <Box 
    display="flex" 
    flexDirection="column"
    justifyContent="center" 
    alignItems="center" 
    minHeight="100vh"
    gap={2}
  >
    <CircularProgress size={60} />
    <Typography variant="h6">{message}</Typography>
  </Box>
);

// 메인 앱 컴포넌트 (Keycloak 컨텍스트 내부)
const MainApp = () => {
  const { isAuthenticated, isLoading, user, error } = useKeycloak();
  const [configError, setConfigError] = useState(null);
  const [amplifyConfigured, setAmplifyConfigured] = useState(false);

  useEffect(() => {
    // AWS Amplify 설정 (Storage만 사용)
    try {
      const config = getAwsConfig();
      console.log('Configuring Amplify with:', {
        region: config.Storage.S3.region,
        s3Bucket: config.Storage.S3.bucket
      });
      
      Amplify.configure(config);
      setAmplifyConfigured(true);
    } catch (error) {
      console.error('Error configuring Amplify:', error);
      setConfigError(error.message);
    }
  }, []);

  // Keycloak 로딩 중
  if (isLoading) {
    return <LoadingScreen message="인증 시스템을 초기화하고 있습니다..." />;
  }

  // Keycloak 오류
  if (error) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        p={3}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            인증 오류
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
      </Box>
    );
  }

  // AWS 설정 오류
  if (configError) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        p={3}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            AWS 설정 오류
          </Typography>
          <Typography variant="body2">
            {configError}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            AWS 설정과 환경 변수를 확인해주세요.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // AWS 설정 로딩 중
  if (!amplifyConfigured) {
    return <LoadingScreen message="AWS 설정을 로드하고 있습니다..." />;
  }

  // 인증되지 않은 경우 (Keycloak이 자동으로 로그인 페이지로 리다이렉트)
  if (!isAuthenticated) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6">로그인이 필요합니다</Typography>
        <Typography variant="body2" color="text.secondary">
          잠시 후 로그인 페이지로 이동합니다...
        </Typography>
      </Box>
    );
  }

  // 인증 완료 - 메인 앱 렌더링
  return (
    <Routes>
      <Route 
        path="/" 
        element={<S3Browser user={user} />} 
      />
      <Route 
        path="/callback" 
        element={<LoginCallback />} 
      />
      <Route 
        path="/debug" 
        element={<KeycloakDebug />} 
      />
      <Route 
        path="*" 
        element={<Navigate to="/" replace />} 
      />
    </Routes>
  );
};

// 최상위 App 컴포넌트
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <KeycloakProvider>
        <Router>
          <div className="App">
            <MainApp />
          </div>
        </Router>
      </KeycloakProvider>
    </ThemeProvider>
  );
}

export default App;
